/**
 * Alert Banner Module
 *
 * Handles dismiss functionality for info-type alerts using sessionStorage.
 * Warning-type alerts cannot be dismissed.
 * Dismissed state persists for the browser session only.
 */

module.exports = class AlertBanner {

	static get alertSelector() {
		return '.alert-banner';
	}

	static get closeButtonSelector() {
		return '.alert-banner__close';
	}

	static get storageKeyPrefix() {
		return 'truett_alert_dismissed_';
	}

	constructor() {
		document.addEventListener('DOMContentLoaded', () => {
			this.initializeAlerts();
		});
	}

	initializeAlerts() {
		const alertElements = document.querySelectorAll(this.constructor.alertSelector);

		alertElements.forEach((alert) => {
			const alertId = alert.dataset.alertId;
			const isDismissible = alert.dataset.dismissible === 'true';

			if (!alertId) {
				return;
			}

			// Check if this alert was previously dismissed
			if (isDismissible && this.isAlertDismissed(alertId)) {
				this.hideAlert(alert);
				return;
			}

			// Set up close button event listener
			if (isDismissible) {
				const closeButton = alert.querySelector(this.constructor.closeButtonSelector);
				if (closeButton) {
					closeButton.addEventListener('click', () => {
						this.dismissAlert(alert, alertId);
					});

					// Handle keyboard accessibility
					closeButton.addEventListener('keydown', (event) => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							this.dismissAlert(alert, alertId);
						}
					});
				}
			}
		});
	}

	/**
	 * Check if an alert has been dismissed in the current session.
	 *
	 * @param {string} alertId - Unique identifier for the alert
	 * @returns {boolean} - True if alert is dismissed
	 */
	isAlertDismissed(alertId) {
		try {
			const storageKey = this.constructor.storageKeyPrefix + alertId;
			return sessionStorage.getItem(storageKey) === 'true';
		} catch (e) {
			// sessionStorage may be unavailable (private browsing, etc.)
			return false;
		}
	}

	/**
	 * Dismiss an alert and store the state in sessionStorage.
	 *
	 * @param {HTMLElement} alert - The alert element
	 * @param {string} alertId - Unique identifier for the alert
	 */
	dismissAlert(alert, alertId) {
		try {
			const storageKey = this.constructor.storageKeyPrefix + alertId;
			sessionStorage.setItem(storageKey, 'true');
		} catch (e) {
			// sessionStorage may be unavailable
		}

		this.hideAlert(alert);
	}

	/**
	 * Hide an alert element with proper ARIA handling.
	 *
	 * @param {HTMLElement} alert - The alert element to hide
	 */
	hideAlert(alert) {
		alert.setAttribute('aria-hidden', 'true');
		alert.style.display = 'none';

		// Dispatch custom event for potential integrations
		const event = new CustomEvent('alertBannerDismissed', {
			detail: { alertId: alert.dataset.alertId },
			bubbles: true
		});
		alert.dispatchEvent(event);
	}
};
